/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { DomController } from '../services/DomController';

/**
 * Centralized status management for consistent user feedback
 * Provides standardized status message patterns and error handling
 */
export class StatusManager {
  private dom: DomController;

  constructor(dom: DomController) {
    this.dom = dom;
  }

  /**
   * Show loading status
   * @param operation - Operation being performed
   */
  loading(operation: string): void {
    this.dom.updateStatus(`${operation}...`);
  }

  /**
   * Show success status
   * @param operation - Operation that completed
   */
  success(operation: string): void {
    this.dom.updateStatus(`${operation} complete.`);
  }

  /**
   * Show error status and log error
   * @param operation - Operation that failed
   * @param error - Optional error object
   */
  error(operation: string, error?: Error): void {
    console.error(`Error ${operation.toLowerCase()}:`, error);
    this.dom.updateStatus(`Failed to ${operation.toLowerCase()}.`);
  }

  /**
   * Show progress status with detail
   * @param operation - Current operation
   * @param detail - Progress detail
   */
  progress(operation: string, detail: string): void {
    this.dom.updateStatus(`${operation}: ${detail}`);
  }

  /**
   * Show warning status
   * @param message - Warning message
   */
  warning(message: string): void {
    console.warn(`Warning: ${message}`);
    this.dom.updateStatus(message);
  }

  /**
   * Show ready status
   */
  ready(): void {
    this.dom.updateStatus('Ready');
  }

  /**
   * Show custom status message
   * @param message - Custom message
   */
  custom(message: string): void {
    this.dom.updateStatus(message);
  }
}
